import {
  CloseAction,
  CloseActionType,
  closeActionMayNavigate,
} from "@redotech/redo-content-builder-common/element-schema";
import { parseNumericIdFromShopifyId } from "@redotech/shopify-util/id";
import { assertNever } from "@redotech/util/type";

function getShopifyRoutes(): string {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return (globalThis as any).Shopify?.routes?.root ?? "/";
}

/**
 * Applies a discount code to the Shopify cart using the Ajax API.
 */
async function applyDiscountToCart(discountCode: string): Promise<void> {
  const response = await fetch(`${getShopifyRoutes()}cart/update.js`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ discount: discountCode }),
  });
  if (!response.ok) {
    console.error("Failed to apply discount to cart", await response.text());
  }
}

/**
 * Navigates to the Shopify cart page.
 */
function openCart(): void {
  window.location.href = `${window.location.origin}/cart`;
}

/**
 * Navigates to the Shopify checkout page.
 */
function openCheckout(): void {
  window.location.href = `${window.location.origin}/checkout`;
}

/**
 * Adds a product variant to the Shopify cart using the Ajax API.
 */
async function addProductToCart(
  variantId: string,
  quantity: number,
  properties?: Record<string, string>,
): Promise<void> {
  const numericId = parseNumericIdFromShopifyId(variantId);
  const response = await fetch(`${getShopifyRoutes()}cart/add.js`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      items: [{ id: Number(numericId), quantity, properties }],
    }),
  });
  if (!response.ok) {
    console.error("Failed to add product to cart", await response.text());
  }
}

export interface CloseActionContext {
  /** Map of discountId -> discountCode for resolved button action discounts */
  resolvedDiscounts?: Record<string, string>;
  /** Subtext to show below buttons that navigate to checkout (e.g. "(Checkout+ included)") */
  buttonSubtext?: string;
  /**
   * Run before a close action that may send the shopper to checkout
   * (OPEN_CHECKOUT, or a CLICK_ELEMENT whose handler navigates). Used to flush
   * work that would be lost once the page navigates away — e.g. add the
   * coverage product to cart, update cart attributes, log CTA analytics. Not
   * run for OPEN_CART (viewing the cart is not proceeding to purchase).
   */
  beforePossibleCheckoutNavigation?: () => Promise<void>;
}

/**
 * Executes a single close action.
 */
async function executeCloseAction(
  action: CloseAction,
  context: CloseActionContext,
): Promise<void> {
  switch (action.type) {
    case CloseActionType.ADD_DISCOUNT_TO_CART: {
      const discountCode = context.resolvedDiscounts?.[action.discountId];
      if (discountCode) {
        await applyDiscountToCart(discountCode);
      } else {
        console.warn(
          "ADD_DISCOUNT_TO_CART action triggered but no discount code available for discountId:",
          action.discountId,
        );
      }
      break;
    }
    case CloseActionType.OPEN_CART:
      openCart();
      break;
    case CloseActionType.OPEN_CHECKOUT:
      if (context.beforePossibleCheckoutNavigation) {
        await context.beforePossibleCheckoutNavigation();
      }
      openCheckout();
      break;
    case CloseActionType.ADD_PRODUCT_TO_CART:
      if (action.productId && action.variantId) {
        await addProductToCart(action.variantId, action.quantity ?? 1);
      } else {
        console.warn(
          "ADD_PRODUCT_TO_CART action triggered but no variantId available",
          action,
        );
      }
      break;
    case CloseActionType.CLICK_ELEMENT: {
      // The selector is merchant-authored free text. An invalid (or empty)
      // selector makes querySelector throw a SyntaxError, so guard it and fall
      // back to the same warn + no-op path as an unmatched element.
      let element: HTMLElement | null = null;
      try {
        element = document.querySelector<HTMLElement>(action.selector);
      } catch {
        console.warn(
          "CLICK_ELEMENT action triggered with an invalid selector:",
          action.selector,
        );
        break;
      }
      if (!element) {
        console.warn(
          "CLICK_ELEMENT action triggered but no element matched selector:",
          action.selector,
        );
        break;
      }
      // The merchant's own click handler may navigate away (e.g. an
      // add-to-cart that redirects straight to checkout), which would kill any
      // in-flight instrumentation. Run checkout prep / analytics first and
      // await it before handing control to their handler.
      if (context.beforePossibleCheckoutNavigation) {
        await context.beforePossibleCheckoutNavigation();
      }
      element.click();
      break;
    }
    default:
      assertNever(action);
  }
}

/**
 * Executes an array of close actions in sequence.
 * Navigating actions (OPEN_CART, OPEN_CHECKOUT, CLICK_ELEMENT) run last since
 * they change the page and anything after them wouldn't run.
 */
export async function executeCloseActions(
  actions: CloseAction[],
  context: CloseActionContext,
): Promise<void> {
  // Sort actions so navigating actions happen last (they change the page, so
  // anything after them wouldn't run). CLICK_ELEMENT counts here because the
  // merchant's own handler may navigate (e.g. add-to-cart that goes straight
  // to checkout) — ordering it last is correct when it does and harmless when
  // it doesn't.
  const sortedActions = [...actions].sort((a, b) => {
    const aMayNavigate = closeActionMayNavigate(a.type);
    const bMayNavigate = closeActionMayNavigate(b.type);
    if (aMayNavigate && !bMayNavigate) {
      return 1;
    }
    if (!aMayNavigate && bMayNavigate) {
      return -1;
    }
    return 0;
  });

  for (const action of sortedActions) {
    await executeCloseAction(action, context);
  }
}
